/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	type ElementRef,
	EventEmitter,
	Output,
	TemplateRef,
	contentChild,
	effect,
	forwardRef,
	input,
	linkedSignal,
	signal,
	viewChild,
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import Fuse from "fuse.js";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { PopoverModule } from "../floating/popover/popover.module";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => InputComponent),
	multi: true,
};

export interface CbInputOption {
	label: string;
	value: string;
}

@Component({
	selector: "cb-input",
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		IconComponent,
		NgxMaskDirective,
		TooltipModule,
		PopoverModule,
		TranslateModule,
	],
	templateUrl: "./input.component.html",
	styleUrl: "./input.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, provideNgxMask()],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements ControlValueAccessor {
	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * If the input is click only
	 */
	public readonly isClickOnly = input(false);

	/**
	 * If the input is read only
	 */
	public readonly isReadonly = input(false);

	/**
	 * The mask of the input.
	 * Uses ngx-mask as library. Documentation can be found [here](https://www.npmjs.com/package/ngx-mask)
	 */
	public readonly mask = input<string>();

	/**
	 * The type of the input
	 */
	public readonly type = input<("text" | "password" | "email") | undefined>(
		"text",
	);

	/**
	 * Prefix symbol of the input
	 */
	public readonly prefix = input<string>();

	/**
	 * Suffix symbol of the input
	 */
	public readonly suffix = input<string>();

	/**
	 * Leading text of the input (ex. http://)
	 */
	public readonly leadingText = input<string>();

	/**
	 * Trailing text of the input (ex. .com)
	 */
	public readonly trailingText = input<string>();

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input<string>();

	/**
	 * If the input should show the eye icon
	 * when the type is password
	 */
	public readonly showEyeIcon = input(true);

	/**
	 * If the input should have a reset button
	 */
	public readonly resetButton = input(false);

	/**
	 * Leading dropdown options of the input
	 * displayed only when these are defined
	 */
	public leadingDropdownOptions = input<CbInputOption[]>([]);

	/**
	 * Trailing dropdown options of the input
	 * displayed only when these are defined
	 */
	public trailingDropdownOptions = input<CbInputOption[]>([]);

	/**
	 * Whether the input should have a copy button
	 */
	public readonly copyButton = input<boolean>();

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * If the input should be automatically focused
	 */
	public readonly autofocus = input<boolean>();

	/**
	 * HTML autocomplete standard attribute
	 */
	public readonly autocomplete = input<string>();

	/**
	 * HTML name standard attribute
	 */
	public readonly name = input<string>();

	/**
	 * The max length of the input
	 */
	public readonly maxLength = input<number>();

	/**
	 * If the leading template is clickable
	 */
	public readonly leadingTemplateClickable = input(false);

	/**
	 * If the trailing template is clickable
	 */
	public readonly trailingTemplateClickable = input(false);

	/**
	 * Reference to the prefix element
	 */
	readonly prefixElement = viewChild<ElementRef>("Prefix");

	/**
	 * Reference to the input element
	 */
	readonly inputElement = viewChild.required<ElementRef>("Input");

	/**
	 * Reference to the suffix element
	 */
	readonly suffixElement = viewChild<ElementRef>("Suffix");

	/**
	 * Leading template reference
	 */
	readonly leadingTemplate = contentChild("LeadingTemplate", {
		read: TemplateRef,
	});

	/**
	 * Trailing template reference
	 */
	readonly trailingTemplate = contentChild("TrailingTemplate", {
		read: TemplateRef,
	});

	/**
	 * Leading icon template reference
	 */
	readonly leadingIcon = contentChild("LeadingIcon", { read: TemplateRef });

	/**
	 * Trailing icon template reference
	 */
	readonly trailingIcon = contentChild("TrailingIcon", { read: TemplateRef });

	/**
	 * Emits the value of the input on input
	 */
	@Output() public input: EventEmitter<string> = new EventEmitter<string>();

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<string | null> = new EventEmitter<
		string | null
	>();
	/**
	 * Emits the value of the input on change
	 */
	@Output() public blur: EventEmitter<void> = new EventEmitter<void>();

	/**
	 * Emits when the user clicks enter
	 */
	@Output() public enter: EventEmitter<Event> = new EventEmitter<Event>();

	@Output() public leadingTemplateClick: EventEmitter<void> =
		new EventEmitter<void>();

	@Output() public trailingTemplateClick: EventEmitter<void> =
		new EventEmitter<void>();

	@Output() public inputClick: EventEmitter<MouseEvent> =
		new EventEmitter<MouseEvent>();

	/**
	 * Handles the password visibility
	 */
	public passwordShown = false;

	/**
	 * The internal data model
	 */
	protected value = signal<string | null>(null);

	public filteredLeadingDropdownOptions = signal<CbInputOption[]>([]);
	public filteredTrailingDropdownOptions = signal<CbInputOption[]>([]);

	public leadingDropdownSearch = signal("");
	public trailingDropdownSearch = signal("");

	public leadingDropdownValue = signal<CbInputOption | undefined>(undefined);
	public trailingDropdownValue = signal<CbInputOption | undefined>(undefined);

	public readonly isDisabled = linkedSignal(() => this.disabled());

	constructor() {
		effect(() => {
			this.filteredLeadingDropdownOptions.set(this.leadingDropdownOptions());
		});

		effect(() => {
			this.filteredTrailingDropdownOptions.set(this.trailingDropdownOptions());
		});

		effect(() => {
			this.emitChanges();
		});

		effect(() => {
			const inputElement = this.inputElement();
			const autofocus = this.autofocus();

			if (inputElement && autofocus) {
				setTimeout(() => {
					this.focusInput();
				}, 0);
			}
		});
	}

	ngOnInit() { }

	/**
	 * Returns the current type of the input
	 */
	public get currentType(): "text" | "password" | "email" {
		const type = this.type();
		if (type === "password" && this.passwordShown) {
			return "text";
		}

		return type ?? "text";
	}

	/**
	 * Calculates the mask of the input to show
	 * as placeholder
	 */
	public get shownMaskExpression(): string | undefined {
		// Put "_" as placeholder for each mask character
		// replacing "A", digits and "S" and leave the rest as it is
		return this.mask()?.replace(/[AS\d]/g, "_");
	}

	public get selectedTrailingText(): string | undefined {
		const trailingDropdownValue = this.trailingDropdownValue();
		if (trailingDropdownValue) {
			return trailingDropdownValue.label;
		}

		return "";
	}

	public get selectedLeadingText(): string | undefined {
		const leadingDropdownValue = this.leadingDropdownValue();
		if (leadingDropdownValue) {
			return leadingDropdownValue.label;
		}

		return "";
	}

	public writeValue(obj: string | null): void {

		if (obj !== this.value()) {
			let value = obj;

			if (value) {
				if (this.leadingDropdownOptions()?.length > 0) {
					const leadingOptions = structuredClone(
						this.leadingDropdownOptions(),
					).sort((a, b) => {
						return b.value.length - a.value.length;
					});
					const leadingValue = leadingOptions.find((option) =>
						value?.startsWith(option.value),
					);
					if (leadingValue) {
						this.leadingDropdownValue.set(leadingValue);
						value = value.slice(leadingValue.value.length);
					}
				}

				if (this.trailingDropdownOptions()?.length > 0) {
					const trailingOptions = structuredClone(
						this.trailingDropdownOptions(),
					).sort((a, b) => {
						return b.value.length - a.value.length;
					});
					const trailingValue = trailingOptions.find((option) =>
						value?.endsWith(option.value),
					);
					if (trailingValue) {
						this.trailingDropdownValue.set(trailingValue);
						value = value.slice(0, value.length - trailingValue.value.length);
					}
				}
			}

			if (!value || value.length === 0) {
				this.value.set(null);
			} else {
				this.value.set(value);
			}
		}
	}

	public focusInput(): void {
		requestAnimationFrame(() => {
			this.inputElement().nativeElement.focus();
		});
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public selectLeadingValue(value: CbInputOption): void {
		this.leadingDropdownValue.set(value);
		this.emitChanges();
	}

	public selectTrailingValue(value: CbInputOption): void {
		this.trailingDropdownValue.set(value);
		this.emitChanges();
	}

	private emitChanges(): void {
		let v = this.value();

		if (v && v.length > 0) {
			const leadingDropdownValue = this.leadingDropdownValue();
			if (leadingDropdownValue) {
				v = leadingDropdownValue.value + v;
			}

			const trailingDropdownValue = this.trailingDropdownValue();
			if (trailingDropdownValue) {
				v += trailingDropdownValue.value;
			}
		}

		this.onChangeCallback(v);
		this.change.emit(this.value());
	}

	public filterLeadingDropdown(): void {
		if (this.leadingDropdownSearch().length === 0) {
			this.filteredLeadingDropdownOptions.set(this.leadingDropdownOptions());
			return;
		}

		const fuse = new Fuse(this.leadingDropdownOptions(), {
			keys: ["label"],
			threshold: 0.4,
		});

		this.filteredLeadingDropdownOptions.set(
			fuse.search(this.leadingDropdownSearch()).map((result) => result.item),
		);
	}

	public filterTrailingDropdown(): void {
		if (this.trailingDropdownSearch().length === 0) {
			this.filteredTrailingDropdownOptions.set(this.trailingDropdownOptions());
			return;
		}

		const fuse = new Fuse(this.trailingDropdownOptions(), {
			keys: ["label"],
			threshold: 0.4,
		});

		this.filteredTrailingDropdownOptions.set(
			fuse.search(this.trailingDropdownSearch()).map((result) => result.item),
		);
	}

	public onLeadingTemplateClick(): void {
		if (!this.leadingTemplateClickable()) return;
		this.leadingTemplateClick.emit();
	}

	public onTrailingTemplateClick(): void {
		console.log("onTrailingTemplateClick", this.trailingTemplateClickable());
		if (!this.trailingTemplateClickable()) return;
		this.trailingTemplateClick.emit();
	}

	public reset(): void {
		this.value.set(null);
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled.set(isDisabled);
	}

	/**
	 * Copies the current text of the input
	 * to the clipboard
	 */
	public copyText(): void {
		// TODO: Implement copy text
		if (navigator.clipboard) {
			navigator.clipboard.writeText(this.value() ?? "").then(
				() => {
					console.log("Link copiato negli appunti!");
				},
				(err) => {
					console.error("Errore nel copiare il testo: ", err);
				},
			);
		} else {
			console.warn("Clipboard API non supportato");
		}
	}
}
